import type { ReactNode } from "react";
import {
  alpha,
  Box,
  Divider,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import moment from "moment";

import Usuario from "../../../../../models/usuario";
import { useUsuario } from "../../../../../providers/usuarioProvider";
import { Perfil } from "../../../../../models/enum";
import MenuCliente from "./menuCliente";
import FlagCliente from "./flagCliente";
import FelixAvatar from "../../../../util/ui/felixAvatar";
import StatusCalculoChip from "./statusCalculoChip";
import type { StatusCalculo } from "./statusCalculoChip";
import { updateDoc } from "firebase/firestore";
import UsuarioDb from "../../../../../db/usuarioDb";

type Props = {
  cliente: Usuario;
};

/** Data sempre em mono tabular, mesmo no meio de uma frase. */
function DataMono({ children }: { children: ReactNode }) {
  return (
    <Typography variant="dataMono" component="span" fontSize="inherit">
      {children}
    </Typography>
  );
}

export default function ItemListaCliente({ cliente }: Props) {
  const { usuario, visualizado } = useUsuario();

  let statusCalculo: StatusCalculo;
  let textoCalculo;

  const atrasado = !!cliente.atualizadoAte?.isBefore(
    moment.tz("America/Sao_Paulo"),
    "day"
  );
  const semCalculo = !cliente?.estatisticas?.impostos;
  if (semCalculo) {
    statusCalculo = "error";
    textoCalculo = "Cálculos ainda não iniciados.";
  } else {
    if (!atrasado) {
      statusCalculo = "success";
      textoCalculo = "Cálculos atualizados.";
    } else {
      statusCalculo = "warning";
      textoCalculo = (
        <>
          {"Cálculos desatualizados desde "}
          <DataMono>{cliente.atualizadoAte.format("DD/MM/YYYY")}</DataMono>.
        </>
      );
    }
  }

  const selecionado = visualizado === cliente.id;

  return (
    <>
      <ListItem
        alignItems="flex-start"
        sx={(theme) => ({
          width: "100%",
          // Linha selecionada: tint accent + borda esquerda 2px accent.
          backgroundColor: selecionado
            ? alpha(theme.palette.primary.main, 0.08)
            : theme.palette.background.paper,
          borderLeft: "2px solid",
          borderLeftColor: selecionado ? theme.palette.accentInk : "transparent",
        })}
        secondaryAction={
          <Box display="flex" flexDirection="column">
            <FlagCliente cliente={cliente} />
            <MenuCliente cliente={cliente} />
          </Box>
        }
      >
        <ListItemAvatar
          sx={{ cursor: "pointer" }}
          onClick={() =>
            usuario &&
            updateDoc(new UsuarioDb().doc({ usuarioId: usuario?.id }), {
              asUser: cliente.id,
            })
          }
        >
          <FelixAvatar
            nome={cliente.nome}
            size={40}
            perfil={Perfil.INVESTIDOR}
            fotoDePerfil={cliente.fotoDePerfil}
          />
        </ListItemAvatar>
        <ListItemText
          sx={{ flexGrow: 1 }}
          primaryTypographyProps={{ component: "div" }}
          secondaryTypographyProps={{ component: "div" }}
          primary={
            <Box
              display="flex"
              flexDirection="row"
              alignItems="baseline"
              flexWrap="wrap"
              columnGap={2}
              pb={0.5}
            >
              <Typography variant="h4" component="span" color="text.primary">
                {cliente.nome}
              </Typography>
              <Typography
                variant="dataMono"
                component="span"
                color="text.secondary"
              >
                {cliente.cpf ?? "-"}
              </Typography>
            </Box>
          }
          secondary={
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              flexWrap="wrap"
              columnGap={1.5}
              width="100%"
            >
              <StatusCalculoChip status={statusCalculo} />
              <Typography
                noWrap
                component="span"
                variant="body2"
                color="text.secondary"
              >
                {textoCalculo}
              </Typography>
            </Box>
          }
        />
      </ListItem>
      <Divider variant="inset" component="li" />
    </>
  );
}
